import { defineStore } from 'pinia'
import type { AddressChain, ContentPage, House, PaginatedFilteredTariffList, ProviderRatings } from '~/api'
import { useCities } from '~/store/cites/cities.store'

export const useTariffs = defineStore('tariffs', {
  state: () => ({
    loadingTariffs: false,
    loadingShowMore: false,
    tariffsByCity: {} as PaginatedFilteredTariffList,
    providerOnProviderPage: {} as ProviderRatings,
    houseID: undefined as number | undefined,
    houseUrl: undefined as string | undefined,
    recentSearch: [] as any[],
    notifArr: [],
    selectedRouter: [],
    selectedTvBox: [],
    quizTariffsInput: {},
    orderedTariff: undefined as string | undefined,
    orderId: undefined as number | undefined,
    passThroughProviderId: null as string | null,
    interactionId: undefined as number | undefined,
    clientPhone: undefined as string | undefined,
    clientName: undefined as string | undefined,
    payments: undefined as string[] | undefined,
    timeToCall: undefined as string | undefined,
    tariffsFilterByHouse: true,
    house: {} as object,
    houseData: {} as House | {},
    addressAncestors: undefined as AddressChain | undefined,
    options: {} as Omit<PaginatedFilteredTariffList, 'results'>,
    linkList: [
      {
        name: 'Интернет',
        url: 'internet',
        id: 1,
        setsId: ['1'],
      },
      {
        name: 'Интернет + ТВ',
        url: 'internet-tv',
        id: 11,
        setsId: ['1', '2'],
      },
      {
        name: 'Интернет + Моб. связь',
        url: 'internet-mobile',
        id: 14,
        setsId: ['1', '4'],
      },
      {
        name: 'Интернет + ТВ + Моб. связь',
        url: 'internet-tv-mobile',
        id: 13,
        setsId: ['1', '2', '4'],
      },
    ],
    smartFilters: [
      {
        smart_filter: {
          slug: 'internet-tv',
          name: 'Интернет + ТВ',
          category: 'SERVICE',
          filters: {
            service: [1, 2],
            for_mono_tv: false,
          },
          description: '',
        },
        service_group: {
          id: 11,
          service_types: [
            {
              id: 1,
              name: 'Интернет',
              slug: 'internet',
            },
            {
              id: 2,
              name: 'ТВ',
              slug: 'tv',
            },
          ],
          name: 'Интернет + ТВ',
          slug: 'internet-tv',
          on_index_page: true,
          on_tariffs_page: true,
          index_page: false,
        },
        index_page: false,
        on_index_page: true,
        on_tariffs_page: true,
        in_header: '',
        in_tabs: '',
        in_footer: '',
        display_row: 1,
      },
      {
        smart_filter: {
          slug: 'internet',
          name: 'Только интернет',
          category: 'SERVICE',
          filters: {
            service: [1],
            for_mono_tv: false,
          },
          description: 'Только интернет и ничего лишнего',
        },
        service_group: {
          id: 1,
          service_types: [
            {
              id: 1,
              name: 'Интернет',
              slug: 'internet',
            },
          ],
          name: 'Только интернет',
          slug: 'internet',
          on_index_page: true,
          on_tariffs_page: true,
          index_page: true,
        },
        index_page: true,
        on_index_page: true,
        on_tariffs_page: true,
        in_header: '',
        in_tabs: '',
        in_footer: '',
        display_row: 1,
      },
      {
        smart_filter: {
          slug: 'internet-tv-mobile',
          name: 'Интернет + ТВ + Моб. связь',
          category: 'SERVICE',
          filters: {
            service: [1, 2, 4],
            for_mono_tv: false,
          },
          description: '',
        },
        service_group: {
          id: 13,
          service_types: [
            {
              id: 1,
              name: 'Интернет',
              slug: 'internet',
            },
            {
              id: 2,
              name: 'ТВ',
              slug: 'tv',
            },
            {
              id: 4,
              name: 'Моб. связь',
              slug: 'mobile',
            },
          ],
          name: 'Интернет + ТВ + Моб. связь',
          slug: 'internet-tv-mobile',
          on_index_page: true,
          on_tariffs_page: true,
          index_page: false,
        },
        index_page: false,
        on_index_page: true,
        on_tariffs_page: true,
        in_header: '',
        in_tabs: '',
        in_footer: '',
        display_row: 1,
      },
      {
        smart_filter: {
          slug: 'subscriptions',
          name: 'С видеосервисами',
          category: 'SPECIAL',
          filters: {
            with_any_subscription: true,
            for_mono_tv: true,
          },
          description: '',
        },
        index_page: false,
        on_index_page: true,
        on_tariffs_page: true,
        in_header: '',
        in_tabs: '',
        in_footer: '',
        display_row: 1,
      },
    ],
    selectedSmartFilter: null as string | null,
    textTariffsPage: {} as ContentPage,
    tariffsCount: undefined as number | undefined,
    tariffsShowCount: undefined as number | undefined,
    closePopover: false,
    favouritesList: {},
    compareList: {},
  }),
  getters: {
    getLoadingTariffs: state => state.loadingTariffs,
    getLoadingShowMore: state => state.loadingShowMore,
    getHouseFullData: state => state.houseData,
    getHouse: state => state.house,
    getHouseId: state => state.house?.houseId,
    getHouseUrl: state => state.house?.autoGeneratedUrl,
    getHouseHash: state => state.house?.hash,
    getHouseType: state => state.house?.houseType,
    getPassThroughProviderId: state => state.passThroughProviderId,
    getRecentSearch: state => state.recentSearch,
    getNotifArr: state => state.notifArr,
    getSelectedRouter: state => state.selectedRouter,
    getSelectedTvBox: state => state.selectedTvBox,
    getQuizTariffsInput: state => state.quizTariffsInput,
    getOrderId: state => state.orderId,
    getInteractionId: state => state.interactionId,
    getOrderedTariff: state => state.orderedTariff,
    getClientPhone: state => state.clientPhone,
    getClientName: state => state.clientName,
    getPayments: state => state.payments,
    getTimeToCall: state => state.timeToCall,
    getTariffsFilterByHouse: state => state.tariffsFilterByHouse,
    getAddressAncestors: state => state.addressAncestors,
    getTariffsInCity: state => state.tariffsByCity,
    getOptionsWithCount: state => state.options,
    getSmartFilters: state => state.smartFilters,
    getLinkList: state => state.linkList,
    getSelectedSmartFilter: state => state.selectedSmartFilter,
    getTextTariffsPage: state => state.textTariffsPage,
    getProviderOnProviderPage: state => state.providerOnProviderPage,
    getTariffsCount: state => state.tariffsCount,
    getTariffsShowCount: state => state.tariffsShowCount,
    getClosePopover: state => state.closePopover,
    getFavouritesList: state => state.favouritesList,
    getCompareList: state => state.compareList,
    getAddressText: (state) => {
      const indexRoot = state.addressAncestors?.address_objects?.find(
        (el: any) => el.url_root,
      )
        ? state.addressAncestors.address_objects.indexOf(
          state.addressAncestors.address_objects.find(
            (el: any) => el.url_root,
          )!,
        ) + 1
        : state.addressAncestors?.address_objects.length || undefined
      const currentArea = state.addressAncestors?.address_objects?.find(
        el => el.level === 'region',
      )
      const cityIndex = state.addressAncestors?.address_objects?.find(
        (el: any) => el.level === 'city',
      )
        ? state.addressAncestors.address_objects.indexOf(
          state.addressAncestors.address_objects.find(
            (el: any) => el.level === 'city',
          )!,
        )
        : state.addressAncestors?.address_objects?.find(
          (el: any) => el.level === 'street',
        )
          ? state.addressAncestors.address_objects.indexOf(
            state.addressAncestors.address_objects.find(
              (el: any) => el.level === 'street',
            )!,
          )
          : state.addressAncestors?.address_objects?.length || undefined

      const addressObjects
        = state.addressAncestors?.address_objects
          .slice(0, indexRoot)
          .reverse()
          ?.filter((el: any) => el.level !== 'area')
          .map(
            (el: any) =>
              (el.short_name && typeof el.short_name === 'string'
                ? el.short_name.replace(/-/g, '\u2011') + '\u00A0'
                : '') + (el.name ? el.name.replace(/-/g, '\u2011') : ''),
          ) || []
      const addressObjectsStreets
        = state.addressAncestors?.address_objects
          .slice(0)
          .reverse()
          ?.filter((el: any) => el.level === 'street')
          .map(
            (el: any) =>
              (el.short_name && typeof el.short_name === 'string'
                ? el.short_name.replace(/-/g, '\u2011') + '\u00A0'
                : '') + (el.name ? el.name.replace(/-/g, '\u2011') : ''),
          ) || []

      const cityUrl = useCities().getCity?.url || ''

      const house = state.house?.autoGeneratedUrl ? state.house : ''

      const fullAddress
        = addressObjects?.join(', ')
        + (house ? ', ' + house.houseNumber.replace(/-/g, '\u2011') : '')
      const street = addressObjects?.join(', ')
      const streetOnly = addressObjectsStreets.join(', ')
      const houseOnly = house?.houseNumber?.replace(/-/g, '\u2011')

      const houseAddress
        = state.addressAncestors?.address_objects
          .slice(0, cityIndex || 1)
          .reverse()
          .map(
            (el: any) =>
              (el.short_name && typeof el.short_name === 'string'
                ? el.short_name.replace(/-/g, '\u2011') + '\u00A0'
                : '') + (el.name ? el.name.replace(/-/g, '\u2011') : ''),
          )
          .join(', ')
          + (house ? ', ' + house.houseNumber.replace(/-/g, '\u2011') : '')

      const cityObjects = state.addressAncestors?.address_objects
        .slice(cityIndex ? cityIndex + 1 : 1, indexRoot)
        .reverse()
        ?.filter((el: any) => el.level !== 'area')
      const cityAddress = addressObjects
        ? addressObjects
          ?.filter((el: any) => el.level !== 'area')
          .map(
            (el: any) =>
              (el.short_name && typeof el.short_name === 'string'
                ? el.short_name.replace(/-/g, '\u2011') + '\u00A0'
                : '') + (el.name ? el.name.replace(/-/g, '\u2011') : ''),
          )
          .join(', ')
        : ''
      const cityArea = cityObjects?.length
        ? ' '
        + cityObjects.map(
          (el: any) =>
            (el.short_name && typeof el.short_name === 'string'
              ? el.short_name.replace(/-/g, '\u2011') + '\u00A0'
              : '') + (el.name ? el.name.replace(/-/g, '\u2011') : ''),
        )
        : ''

      const currentCity = state.addressAncestors?.address_objects.slice(
        cityIndex,
        indexRoot,
      )?.[0]
      const cityOnly = currentCity
        ? (cityObjects?.length
            ? cityObjects.map((el: any) =>
              el.short_name && typeof el.short_name === 'string'
                ? el.short_name.replace(/-/g, '\u2011') + '\u00A0'
                : '',
            )
            : '')
            + currentCity.name.replace(/\s/g, '\u00A0').replace(/-/g, '\u2011')
        : ''
      const areaOnly = currentArea ? currentArea.full_name : cityOnly
      const cityOnlyLoc
        = currentCity && currentCity.morph_name?.['где']
          ? currentCity.morph_name?.['где']
            ?.replace(/\s/g, '\u00A0')
            .replace(/-/g, '\u2011') + cityArea
          : currentCity
            ? (currentCity.short_name && !currentCity.name_loc
                ? currentCity.short_name + ' '
                : '')
                + (currentCity.name_loc
                  ? currentCity.name_loc
                    .replace(/\s/g, '\u00A0')
                    .replace(/-/g, '\u2011') + cityArea
                  : currentCity.name
                    .replace(/\s/g, '\u00A0')
                    .replace(/-/g, '\u2011'))
            : ''
      const areaOnlyLoc = currentArea?.morph_name?.['где']
        ? currentArea?.morph_name?.['где']
          ?.replace(/\s/g, '\u00A0')
          .replace(/-/g, '\u2011')
        : cityOnlyLoc
      const cityOnlyPos
        = currentCity && currentCity.morph_name?.['Р']
          ? currentCity.morph_name?.['Р']
            ?.replace(/\s/g, '\u00A0')
            .replace(/-/g, '\u2011') + cityArea
          : currentCity
            ? (currentCity.short_name && !currentCity.name_loc
                ? currentCity.short_name + ' '
                : '')
                + (currentCity.name_loc
                  ? currentCity.name_loc
                    .replace(/\s/g, '\u00A0')
                    .replace(/-/g, '\u2011') + cityArea
                  : currentCity.name
                    .replace(/\s/g, '\u00A0')
                    .replace(/-/g, '\u2011'))
            : ''
      const areaOnlyPos = currentArea?.morph_name?.['Р']
        ? currentArea?.morph_name?.['Р']
          ?.replace(/\s/g, '\u00A0')
          .replace(/-/g, '\u2011')
        : cityOnlyPos

      return {
        cityIndex,
        indexRoot,
        currentArea,
        areaOnly,
        areaOnlyLoc,
        areaOnlyPos,
        cityArea,
        cityObjects,
        houseAddress,
        house,
        fullAddress,
        addressObjects,
        cityAddress,
        street,
        streetOnly,
        houseOnly,
        cityOnly,
        cityOnlyLoc,
        cityOnlyPos,
        cityUrl,
        currentCity,
      }
    },
  },
  actions: {
    setCompare(id: string | number) {
      const cityFias = useCities().getCity.fias_id
      const compare = useCookie('compare') as any
      compare.value = compare.value || {}
      const arr
        = compare.value && compare.value[cityFias] ? compare.value[cityFias] : []

      if (arr && !arr.includes(id)) {
        arr.push(id)
      }
      else if (arr && arr.includes(id)) {
        const index = arr.indexOf(id)
        arr.splice(index, 1)
      }
      compare.value[cityFias] = arr
      const compareCoockie = useCookie('compare')
      compareCoockie.value = compare.value

      this.compareList = compare.value
    },
  },
})
